import { useState } from "react";
import { toast } from "sonner";
import { Phone, Users, Calendar as CalIcon, MapPin, User, Car } from "lucide-react";
import { api } from "../../lib/api";
import { useSettings } from "../../context/SettingsContext";
import { buildWhatsAppLink } from "../../lib/whatsapp";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { WhatsAppIcon } from "./Icons";

const BOARDING = ["Hyderabad", "Vijayawada", "Guntur", "Kurnool", "Anantapur", "Tirupati", "Warangal", "Other"];
const TRIP_TYPES = ["One Way", "Round Trip", "One Day Srisailam Tour", "Customized Tour"];
const VEHICLES = ["Sedan (Dzire / Etios)", "SUV (Ertiga / Innova)", "Premium SUV (Innova Crysta)", "Tempo Traveller (12+ seater)"];

export default function BookingForm({ compact = false, defaultTripType = "One Day Srisailam Tour", trip = null }) {
  const { settings } = useSettings();
  const [form, setForm] = useState({
    name: "", phone: "", boarding_point: "Hyderabad", preferred_date: "",
    travellers: 2, vehicle: VEHICLES[1], trip_type: defaultTripType, message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) { toast.error("Please add your name and mobile number."); return; }
    setSubmitting(true);
    try {
      const payload = {
        name: form.name, phone: form.phone, boarding_point: form.boarding_point,
        preferred_date: form.preferred_date, travellers: Number(form.travellers),
        trip_id: trip?.id, trip_title: trip?.title || form.trip_type,
        message: `Trip type: ${form.trip_type} · Vehicle: ${form.vehicle}${form.message ? " · " + form.message : ""}`,
      };
      await api.post("/enquiries", payload);
      toast.success("Enquiry received — opening WhatsApp with your details.");
      const waMsg = `Namaste RSK Tours,\n\nI would like a quote for:\nTrip type: ${form.trip_type}\nPickup: ${form.boarding_point}\nDate: ${form.preferred_date || "-"}\nPassengers: ${form.travellers}\nVehicle: ${form.vehicle}\nName: ${form.name}\nMobile: ${form.phone}${form.message ? "\nNote: " + form.message : ""}${trip ? "\nPackage: " + trip.title : ""}`;
      window.open(buildWhatsAppLink(settings?.whatsapp_number, waMsg), "_blank");
      setForm({ ...form, name: "", phone: "", preferred_date: "", message: "" });
    } catch { toast.error("Could not submit. Please try WhatsApp directly."); }
    finally { setSubmitting(false); }
  };

  const wa = buildWhatsAppLink();

  return (
    <form onSubmit={submit} className={`bg-white rounded-2xl shadow-card border border-border ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8"}`} data-testid="booking-form">
      {!compact && <h3 className="font-heading text-2xl sm:text-3xl text-navy mb-1">Plan Your Hyderabad to Srisailam Trip</h3>}
      {!compact && <p className="text-muted2 text-sm mb-6">Get a quick quote — we&apos;ll reply on WhatsApp within minutes.</p>}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field icon={MapPin} label="Pickup Location">
          <Select value={form.boarding_point} onValueChange={(v) => setForm({ ...form, boarding_point: v })}>
            <SelectTrigger data-testid="booking-pickup"><SelectValue /></SelectTrigger>
            <SelectContent>{BOARDING.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field icon={CalIcon} label="Travel Date">
          <Input type="date" data-testid="booking-date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} />
        </Field>
        <Field icon={Users} label="Passengers">
          <Input type="number" min="1" data-testid="booking-pax" value={form.travellers} onChange={(e) => setForm({ ...form, travellers: e.target.value })} />
        </Field>
        <Field icon={Car} label="Vehicle Type">
          <Select value={form.vehicle} onValueChange={(v) => setForm({ ...form, vehicle: v })}>
            <SelectTrigger data-testid="booking-vehicle"><SelectValue /></SelectTrigger>
            <SelectContent>{VEHICLES.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field icon={Car} label="Trip Type">
          <Select value={form.trip_type} onValueChange={(v) => setForm({ ...form, trip_type: v })}>
            <SelectTrigger data-testid="booking-triptype"><SelectValue /></SelectTrigger>
            <SelectContent>{TRIP_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </Field>
        <Field icon={User} label="Name">
          <Input data-testid="booking-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your full name" />
        </Field>
        <Field icon={Phone} label="Mobile Number" className="sm:col-span-2">
          <Input inputMode="tel" data-testid="booking-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91…" />
        </Field>
      </div>
      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <Button type="submit" disabled={submitting} data-testid="booking-submit"
          className="w-full sm:flex-1 bg-navy hover:bg-navy-deep text-white text-base h-12 rounded-full">
          {submitting ? "Sending…" : "Get Tour Quote"}
        </Button>
        <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="booking-whatsapp"
          className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1eae55] text-white h-12 px-6 rounded-full text-base transition-colors">
          <WhatsAppIcon className="w-5 h-5" /> WhatsApp
        </a>
      </div>
    </form>
  );
}

function Field({ icon: Icon, label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-wide text-navy/70 flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5 text-gold" /> {label}
      </Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
